import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { documentAPI } from '../services/api';
import SearchableMultiSelect from './SearchableMultiSelect';
import Swal from 'sweetalert2';
import './DocumentManagement.css';

const DocumentManagement = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [activeTab, setActiveTab] = useState('my'); // 'my' hoặc 'units'
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  // Form upload
  const [uploadForm, setUploadForm] = useState({
    title: '',
    file: null,
    recipientUnits: [],
    files: [] // Thêm mảng files để lưu nhiều ảnh
  });
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [availableUnits, setAvailableUnits] = useState([]);
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState(null);
  const videoRef = React.useRef(null);
  const canvasRef = React.useRef(null);



  // QR Code cho danh sách tài liệu
  const [listQrModal, setListQrModal] = useState({
    show: false,
    qrCode: '',
    url: ''
  });

  useEffect(() => {
    fetchDocuments();
    fetchUnits();
  }, [pagination.currentPage, activeTab]);

  const fetchUnits = async () => {
    try {
      const response = await documentAPI.getUnitsForDocument();
      setAvailableUnits(response.data);
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  };

  // Debounce search để tránh gọi API quá nhiều
  useEffect(() => {
    if (searchTerm === '') {
      setIsSearching(false);
      fetchDocuments();
    } else {
      setIsSearching(true);
      const timeoutId = setTimeout(() => {
        setIsSearching(false);
        fetchDocuments();
      }, 500);

      return () => {
        clearTimeout(timeoutId);
        setIsSearching(false);
      };
    }
  }, [searchTerm, activeTab]);

  const fetchDocuments = async () => {
    try {
      // Chỉ hiển thị loading nếu không đang trong quá trình search debounce
      if (!isSearching) {
        setLoading(true);
      }

      const params = {
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        search: searchTerm
      };

      let response;
      if (activeTab === 'my') {
        response = await documentAPI.getMyDocuments(params);
      } else {
        response = await documentAPI.getUnitDocuments(params);
      }

      setDocuments(response.data.documents);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Fetch documents error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Lỗi!',
        text: 'Lỗi khi tải danh sách tài liệu',
        confirmButtonColor: '#1e3a8a'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchTerm('');
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' } // Sử dụng camera sau nếu có
      });
      setStream(mediaStream);
      setShowCamera(true);
      
      // Đợi một chút để video element được render
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      }, 100);
    } catch (error) {
      console.error('Camera error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Lỗi!',
        text: 'Không thể truy cập camera. Vui lòng kiểm tra quyền truy cập.',
        confirmButtonColor: '#1e3a8a'
      });
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      canvas.toBlob((blob) => {
        const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
        
        // Tạo preview URL
        const previewUrl = URL.createObjectURL(blob);
        
        // Thêm vào mảng files
        setUploadForm(prev => ({
          ...prev,
          files: [...prev.files, { file, preview: previewUrl, id: Date.now() }]
        }));
        
        stopCamera();
        
        Swal.fire({
          icon: 'success',
          title: 'Đã chụp ảnh!',
          text: 'Ảnh đã được thêm vào danh sách',
          timer: 1500,
          showConfirmButton: false
        });
      }, 'image/jpeg', 0.9);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    const newFiles = selectedFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      id: Date.now() + Math.random()
    }));
    
    setUploadForm(prev => ({
      ...prev,
      files: [...prev.files, ...newFiles]
    }));
  };

  const removeFile = (fileId) => {
    setUploadForm(prev => ({
      ...prev,
      files: prev.files.filter(f => f.id !== fileId)
    }));
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!uploadForm.title.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Thiếu thông tin',
        text: 'Vui lòng nhập tên tài liệu',
        confirmButtonColor: '#1e3a8a'
      });
      return;
    }

    if (uploadForm.files.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Thiếu thông tin',
        text: 'Vui lòng chọn ít nhất một file hoặc chụp ảnh',
        confirmButtonColor: '#1e3a8a'
      });
      return;
    }

    try {
      setUploading(true);
      
      // Upload từng file
      let successCount = 0;
      let failCount = 0;
      
      for (let i = 0; i < uploadForm.files.length; i++) {
        const fileItem = uploadForm.files[i];
        const formData = new FormData();
        
        // Tên tài liệu: Nếu có nhiều file thì thêm số thứ tự
        const title = uploadForm.files.length > 1 
          ? `${uploadForm.title.trim()} (${i + 1}/${uploadForm.files.length})`
          : uploadForm.title.trim();
        
        formData.append('title', title);
        formData.append('document', fileItem.file);

        // Thêm danh sách đơn vị nhận (nếu có)
        if (uploadForm.recipientUnits.length > 0) {
          formData.append('recipientUnitIds', JSON.stringify(uploadForm.recipientUnits));
        }

        try {
          await documentAPI.uploadDocument(formData);
          successCount++;
        } catch (error) {
          console.error(`Error uploading file ${i + 1}:`, error);
          failCount++;
        }
      }

      setUploadForm({ title: '', file: null, recipientUnits: [], files: [] });
      setShowUploadForm(false);
      fetchDocuments();
      
      if (failCount === 0) {
        Swal.fire({
          icon: 'success',
          title: 'Thành công!',
          text: `Đã upload ${successCount} tài liệu thành công`,
          timer: 2000,
          showConfirmButton: false
        });
      } else {
        Swal.fire({
          icon: 'warning',
          title: 'Hoàn thành một phần',
          html: `
            <p>✅ Thành công: ${successCount} tài liệu</p>
            <p>❌ Thất bại: ${failCount} tài liệu</p>
          `,
          confirmButtonColor: '#1e3a8a'
        });
      }
    } catch (error) {
      console.error('Upload error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Lỗi!',
        text: error.response?.data?.message || 'Lỗi khi upload tài liệu',
        confirmButtonColor: '#1e3a8a'
      });
    } finally {
      setUploading(false);
    }
  };



  const handleView = async (qrCode, originalName) => {
    // Kiểm tra loại file dựa trên extension
    const fileExtension = originalName.toLowerCase().split('.').pop();
    const apiUrl = process.env.REACT_APP_API_URL || 'https://apiqldangkyxe.nguyenluan.vn';

    if (fileExtension === 'pdf') {
      // PDF: Mở trực tiếp trong tab mới
      const pdfUrl = `${apiUrl}/api/documents/public/${qrCode}/download?view=true`;
      window.open(pdfUrl, '_blank');
    } else if (fileExtension === 'doc' || fileExtension === 'docx') {
      // Word: Thông báo và download
      const result = await Swal.fire({
        icon: 'info',
        title: 'File Word',
        text: 'File Word không thể xem trực tiếp trong trình duyệt. Bạn có muốn tải xuống để xem không?',
        showCancelButton: true,
        confirmButtonColor: '#1e3a8a',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Tải xuống',
        cancelButtonText: 'Hủy'
      });
      
      if (result.isConfirmed) {
        const downloadUrl = `${apiUrl}/api/documents/public/${qrCode}/download`;
        window.open(downloadUrl, '_blank');
      }
    } else {
      // Các loại file khác: download
      const downloadUrl = `${apiUrl}/api/documents/public/${qrCode}/download`;
      window.open(downloadUrl, '_blank');
    }
  };

  const handleDelete = async (id, title) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Xác nhận xóa',
      html: `Bạn có chắc chắn muốn xóa tài liệu "<strong>${title}</strong>"?`,
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Xóa',
      cancelButtonText: 'Hủy'
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await documentAPI.deleteDocument(id);
      fetchDocuments();
      
      Swal.fire({
        icon: 'success',
        title: 'Thành công!',
        text: 'Xóa tài liệu thành công',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Delete error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Lỗi!',
        text: error.response?.data?.message || 'Lỗi khi xóa tài liệu',
        confirmButtonColor: '#1e3a8a'
      });
    }
  };



  const handleGenerateListQR = async () => {
    try {
      // Import QRCode library
      const QRCode = require('qrcode');

      // URL để truy cập danh sách tài liệu
      const listUrl = `${window.location.protocol}//${window.location.host}/documents-list`;

      // Tạo QR code
      const qrCodeDataURL = await QRCode.toDataURL(listUrl);

      setListQrModal({
        show: true,
        qrCode: qrCodeDataURL,
        url: listUrl
      });
    } catch (error) {
      console.error('Generate list QR error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Lỗi!',
        text: 'Lỗi khi tạo QR code danh sách',
        confirmButtonColor: '#1e3a8a'
      });
    }
  };

  const handleDownloadQR = () => {
    if (listQrModal.qrCode) {
      // Tạo link tải xuống
      const link = document.createElement('a');
      link.href = listQrModal.qrCode;
      link.download = 'qr-code-danh-sach-tai-lieu.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="document-management">
      <div className="document-header">
        <h2>Quản lý tài liệu</h2>
        <div className="header-actions">
          <button
            className="btn-qr-list"
            onClick={handleGenerateListQR}
          >
            QR Danh sách
          </button>
          {activeTab === 'my' && (
            <button
              className="btn-primary"
              onClick={() => setShowUploadForm(true)}
            >
              Thêm tài liệu
            </button>
          )}
        </div>
      </div>

      {/* Tab navigation */}
      <div className="document-tabs">
        <button
          className={`tab-btn ${activeTab === 'my' ? 'active' : ''}`}
          onClick={() => handleTabChange('my')}
        >
          Tài liệu của tôi
        </button>
        <button
          className={`tab-btn ${activeTab === 'units' ? 'active' : ''}`}
          onClick={() => handleTabChange('units')}
        >
          Tài liệu từ đơn vị
        </button>
      </div>

      {/* Form tìm kiếm */}
      <div className="search-form">
        <div className="search-group">
          <input
            type="text"
            placeholder={activeTab === 'my' ? 'Tìm kiếm tài liệu của tôi...' : 'Tìm kiếm tài liệu từ đơn vị...'}
            value={searchTerm}
            onChange={handleSearchChange}
            className="search-input"
          />
          {isSearching && (
            <div className="search-indicator">
              <span>Đang tìm kiếm...</span>
            </div>
          )}
        </div>
      </div>

      {/* Danh sách tài liệu */}
      {loading ? (
        <div className="loading">Đang tải...</div>
      ) : (
        <>
          <div className="documents-table">
            <table>
              <thead>
                <tr>
                  <th>Tên tài liệu</th>
                  {activeTab === 'units' && <th>Đơn vị gửi</th>}
                  <th>Kích thước</th>
                  <th>Ngày upload</th>
                  <th>Lượt tải</th>
                  <th>Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {documents.length === 0 ? (
                  <tr>
                    <td colSpan={activeTab === 'units' ? 6 : 5} style={{ textAlign: 'center', padding: '20px' }}>
                      {activeTab === 'my' ? 'Bạn chưa upload tài liệu nào' : 'Chưa có tài liệu từ đơn vị'}
                    </td>
                  </tr>
                ) : (
                  documents.map(doc => (
                    <tr key={doc.id}>
                      <td>{doc.title}</td>
                      {activeTab === 'units' && (
                        <td>{doc.uploader?.unit?.name || 'N/A'}</td>
                      )}
                      <td>{formatFileSize(doc.fileSize)}</td>
                      <td>{formatDate(doc.uploadDate)}</td>
                      <td>{doc.downloadCount}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-view"
                            onClick={() => handleView(doc.qrCode, doc.originalName)}
                            title="Xem"
                          >
                            Xem
                          </button>
                          <button
                            className="btn-delete"
                            onClick={() => handleDelete(doc.id, doc.title)}
                            title="Xóa"
                          >
                            Xóa
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Phân trang */}
          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                disabled={pagination.currentPage === 1}
                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
              >
                Trước
              </button>
              <span>
                Trang {pagination.currentPage} / {pagination.totalPages}
              </span>
              <button
                disabled={pagination.currentPage === pagination.totalPages}
                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
              >
                Sau
              </button>
            </div>
          )}
        </>
      )}

      {/* Modal Upload */}
      {showUploadForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Upload tài liệu</h3>
              <button
                className="modal-close"
                onClick={() => {
                  stopCamera();
                  setShowUploadForm(false);
                }}
              >
                ×
              </button>
            </div>
            
            {showCamera ? (
              <div className="camera-container">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className="camera-video"
                />
                <canvas ref={canvasRef} style={{ display: 'none' }} />
                <div className="camera-controls">
                  <button
                    type="button"
                    className="btn-capture"
                    onClick={capturePhoto}
                  >
                    📸 Chụp ảnh
                  </button>
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={stopCamera}
                  >
                    Hủy
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleUpload} className="upload-form">
                <div className="form-group">
                  <label>Tên tài liệu:</label>
                  <input
                    type="text"
                    value={uploadForm.title}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Nhập tên tài liệu..."
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Chọn file hoặc chụp ảnh:</label>
                  <div className="file-upload-options">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,image/*"
                      onChange={handleFileSelect}
                      id="file-input"
                      style={{ display: 'none' }}
                      multiple
                    />
                    <button
                      type="button"
                      className="btn-file-option"
                      onClick={() => document.getElementById('file-input').click()}
                    >
                      📁 Chọn file
                    </button>
                    <button
                      type="button"
                      className="btn-file-option btn-camera"
                      onClick={startCamera}
                    >
                      📷 Chụp ảnh
                    </button>
                  </div>
                  
                  {/* Preview các file đã chọn */}
                  {uploadForm.files.length > 0 && (
                    <div className="files-preview">
                      <div className="preview-header-text">
                        <strong>{uploadForm.files.length} file đã chọn:</strong>
                      </div>
                      <div className="preview-grid">
                        {uploadForm.files.map((fileItem) => (
                          <div key={fileItem.id} className="preview-item">
                            {fileItem.file.type.startsWith('image/') ? (
                              <img src={fileItem.preview} alt="Preview" className="preview-image" />
                            ) : (
                              <div className="preview-file-icon">
                                📄
                                <div className="file-name">{fileItem.file.name}</div>
                              </div>
                            )}
                            <button
                              type="button"
                              className="btn-remove-preview"
                              onClick={() => removeFile(fileItem.id)}
                              title="Xóa"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <small className="file-hint">Hỗ trợ: PDF, DOC, DOCX, JPG, PNG (tối đa 50MB/file). Có thể chọn nhiều file.</small>
                </div>

                <div className="form-group">
                  <label>Gửi cho đơn vị (không bắt buộc):</label>
                  <SearchableMultiSelect
                    options={availableUnits}
                    selectedValues={uploadForm.recipientUnits}
                    onChange={(selectedValues) => setUploadForm(prev => ({ ...prev, recipientUnits: selectedValues }))}
                    placeholder="Chọn đơn vị nhận..."
                    searchPlaceholder="Gõ tên đơn vị để tìm kiếm..."
                  />
                  <small className="units-hint">
                    Không chọn = {user?.role === 'admin' ? 'Gửi cho tất cả đơn vị' : 'Chỉ admin thấy'}
                  </small>
                </div>
                <div className="form-actions">
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={() => setShowUploadForm(false)}
                  >
                    Hủy
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={uploading || uploadForm.files.length === 0}
                  >
                    {uploading ? 'Đang upload...' : `Upload ${uploadForm.files.length} file`}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}



      {/* Modal QR Code cho danh sách */}
      {listQrModal.show && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>QR Code - Danh sách tài liệu</h3>
              <button
                className="modal-close"
                onClick={() => setListQrModal({ show: false, qrCode: '', url: '' })}
              >
                ×
              </button>
            </div>
            <div className="qr-content">
              <div className="qr-code">
                <img src={listQrModal.qrCode} alt="QR Code" />
              </div>
              <div className="qr-info">
                <p><strong>URL truy cập danh sách:</strong></p>
                <div className="url-box">
                  <input
                    type="text"
                    value={listQrModal.url}
                    readOnly
                    onClick={(e) => e.target.select()}
                  />
                </div>
                <p className="qr-note">
                  Quét QR code hoặc truy cập URL để xem danh sách tất cả tài liệu
                </p>
              </div>
              <div className="qr-actions">
                <button
                  className="btn-primary"
                  onClick={handleDownloadQR}
                >
                  Tải QR xuống
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentManagement;
